<template>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">积分数据分析</h1>
        <div class="flex space-x-4">
          <el-select 
            v-model="timeRange" 
            placeholder="选择时间范围"
            class="w-32"
          >
            <el-option label="本周" value="week"></el-option>
            <el-option label="本月" value="month"></el-option>
            <el-option label="本季度" value="quarter"></el-option>
            <el-option label="今年" value="year"></el-option>
          </el-select>
          
          <el-select 
            v-model="departmentId" 
            placeholder="选择部门"
            clearable
            class="w-40"
          >
            <el-option 
              v-for="dept in departmentData.departments" 
              :key="dept.id" 
              :label="dept.name" 
              :value="dept.id.toString()"
            ></el-option>
          </el-select>
        </div>
      </div>

      <!-- 数据概览卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-red-100 mr-4">
              <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 font-medium">积分总量</p>
              <p class="text-2xl font-bold text-gray-800">{{ overviewData.totalScore || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 mr-4">
              <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 font-medium">活跃员工</p>
              <p class="text-2xl font-bold text-gray-800">{{ overviewData.activeEmployees || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 mr-4">
              <svg class="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 font-medium">完成活动</p>
              <p class="text-2xl font-bold text-gray-800">{{ overviewData.completedActivities || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 mr-4">
              <svg class="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 font-medium">人均积分</p>
              <p class="text-2xl font-bold text-gray-800">{{ overviewData.averageScore || 0 }}</p>
            </div>
          </div>
          <div class="mt-2 flex items-center">
            <span 
              :class="[
                'text-sm font-medium', 
                overviewData.growthRate > 0 ? 'text-green-600' : 'text-red-600'
              ]"
            >
              {{ (overviewData.growthRate > 0 ? '+' : '') + (overviewData.growthRate * 100).toFixed(1) }}%
            </span>
            <span class="ml-1 text-sm text-gray-500">较上期</span>
          </div>
        </div>
      </div>

      <!-- 第一行图表 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- 积分趋势图 -->
        <div class="bg-white p-5 rounded-lg shadow">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">积分趋势</h2>
          <div class="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
            <div v-loading="loading" class="w-full h-full" ref="scoreChart"></div>
          </div>
        </div>

        <!-- 积分类别分布图 -->
        <div class="bg-white p-5 rounded-lg shadow">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">积分类别分布</h2>
          <div class="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
            <div v-loading="loading" class="w-full h-full" ref="categoryChart"></div>
          </div>
        </div>
      </div>

      <!-- 第二行图表 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- 部门积分对比 -->
        <div class="bg-white p-5 rounded-lg shadow">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">部门积分对比</h2>
          <div class="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
            <div v-loading="loading" class="w-full h-full" ref="departmentChart"></div>
          </div>
        </div>

        <!-- 积分人员热力图 -->
        <div class="bg-white p-5 rounded-lg shadow">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">员工得分热度</h2>
          <div class="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
            <div v-loading="loading" class="w-full h-full" ref="heatmapChart"></div>
          </div>
        </div>
      </div>

      <!-- 积分详情 -->
      <div class="bg-white p-5 rounded-lg shadow">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-700">积分详情</h2>
          <div class="relative">
            <el-input 
              v-model="searchQuery" 
              placeholder="搜索员工姓名或部门..." 
              prefix-icon="el-icon-search"
              @keyup.enter.native="handleSearch"
              class="w-64"
            >
              <el-button 
                slot="append" 
                icon="el-icon-search"
                @click="handleSearch"
              ></el-button>
            </el-input>
          </div>
        </div>
        <el-table
          v-loading="loading"
          :data="filteredEmployees"
          border
          style="width: 100%"
          :header-cell-style="{backgroundColor: '#f5f7fa', color: '#606266'}"
        >
          <el-table-column label="员工" min-width="150">
            <template slot-scope="scope">
              <div class="flex items-center">
                <div class="w-8 h-8 rounded-full bg-red-100 text-red-600 mr-3 flex items-center justify-center">
                  {{ scope.row.name.charAt(0) }}
                </div>
                {{ scope.row.name }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="departmentName" label="部门" min-width="120"></el-table-column>
          <el-table-column prop="position" label="职位" min-width="100"></el-table-column>
          <el-table-column prop="activitiesJoined" label="参与活动" min-width="100"></el-table-column>
          <el-table-column prop="politicalScore" label="政治积分" min-width="100"></el-table-column>
          <el-table-column prop="professionalScore" label="业务积分" min-width="100"></el-table-column>
          <el-table-column prop="honorScore" label="荣誉积分" min-width="100"></el-table-column>
          <el-table-column prop="totalScore" label="总积分" min-width="100">
            <template slot-scope="scope">
              <span class="font-bold text-gray-900">{{ scope.row.totalScore }}</span>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="flex justify-end mt-6">
          <el-pagination
            @size-change="val => { itemsPerPage = val; currentPage = 1; fetchEmployeeData(); }"
            @current-change="val => { currentPage = val; fetchEmployeeData(); }"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="itemsPerPage"
            layout="total, sizes, prev, pager, next, jumper"
            :total="employeeData.total || 0"
          >
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入 echarts
import * as echarts from 'echarts';
// 导入API
import { 
  getScoreOverview, 
  getScoreTrend, 
  getScoreCategoryDistribution, 
  getDepartmentScoreComparison,
  getEmployeeScoreHeatmap,
  getEmployeeScoreDetails
} from '@/api/analysis';

export default {
  name: 'DataAnalysis',
  data() {
    return {
      timeRange: 'month',
      departmentId: '', // 修改为departmentId
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      loading: false, // 添加加载状态
      
      // 图表实例
      scoreChart: null,
      categoryChart: null,
      departmentChart: null,
      heatmapChart: null,
      
      // 数据
      overviewData: {
        totalScore: 0,
        averageScore: 0,
        activeEmployees: 0,
        completedActivities: 0,
        growthRate: 0
      },
      trendData: {
        labels: [],
        totalScores: [],
        politicalScores: [],
        professionalScores: [],
        honorScores: []
      },
      categoryData: {
        categories: []
      },
      departmentData: {
        departments: []
      },
      heatmapData: {
        heatmapData: [],
        departments: [],
        levels: []
      },
      employeeData: {
        total: 0,
        list: []
      }
    }
  },
  computed: {
    // 计算分页属性
    totalPages() {
      return Math.ceil(this.employeeData.total / this.itemsPerPage);
    },
    // 筛选后的员工数据（本地筛选，实际应该使用服务端筛选）
    filteredEmployees() {
      return this.employeeData.list || [];
    }
  },
  watch: {
    // 监听筛选条件变化
    timeRange() {
      this.fetchAllData();
    },
    departmentId() {
      this.fetchAllData();
    }
  },
  mounted() {
    this.initCharts();
    this.fetchAllData();
  },
  methods: {
    // 初始化所有图表
    initCharts() {
      this.scoreChart = echarts.init(this.$refs.scoreChart);
      this.categoryChart = echarts.init(this.$refs.categoryChart);
      this.departmentChart = echarts.init(this.$refs.departmentChart);
      this.heatmapChart = echarts.init(this.$refs.heatmapChart);
      
      // 监听窗口大小变化，调整图表大小
      window.addEventListener('resize', () => {
        this.scoreChart && this.scoreChart.resize();
        this.categoryChart && this.categoryChart.resize();
        this.departmentChart && this.departmentChart.resize();
        this.heatmapChart && this.heatmapChart.resize();
      });
    },
    
    // 获取所有数据
    async fetchAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchOverviewData(),
          this.fetchTrendData(),
          this.fetchCategoryData(),
          this.fetchDepartmentData(),
          this.fetchHeatmapData(),
          this.fetchEmployeeData()
        ]);
      } catch (error) {
        console.error('获取数据失败:', error);
        this.$message.error('获取数据失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    
    // 获取概览数据
    async fetchOverviewData() {
      try {
        const params = {
          timeRange: this.timeRange,
          departmentId: this.departmentId || ''
        };
        const res = await getScoreOverview(params);
        if (res.code === 200) {
          this.overviewData = res.data;
        }
      } catch (error) {
        console.error('获取概览数据失败:', error);
      }
    },
    
    // 获取趋势数据
    async fetchTrendData() {
      try {
        const params = {
          timeRange: this.timeRange,
          departmentId: this.departmentId || ''
        };
        const res = await getScoreTrend(params);
        if (res.code === 200) {
          this.trendData = res.data;
          this.renderTrendChart();
        }
      } catch (error) {
        console.error('获取趋势数据失败:', error);
      }
    },
    
    // 获取类别分布数据
    async fetchCategoryData() {
      try {
        const params = {
          timeRange: this.timeRange,
          departmentId: this.departmentId || ''
        };
        const res = await getScoreCategoryDistribution(params);
        if (res.code === 200) {
          this.categoryData = res.data;
          this.renderCategoryChart();
        }
      } catch (error) {
        console.error('获取类别分布数据失败:', error);
      }
    },
    
    // 获取部门对比数据
    async fetchDepartmentData() {
      try {
        const params = {
          timeRange: this.timeRange
        };
        const res = await getDepartmentScoreComparison(params);
        if (res.code === 200) {
          this.departmentData = res.data;
          this.renderDepartmentChart();
        }
      } catch (error) {
        console.error('获取部门对比数据失败:', error);
      }
    },
    
    // 获取热力图数据
    async fetchHeatmapData() {
      try {
        const params = {
          timeRange: this.timeRange,
          departmentId: this.departmentId || ''
        };
        const res = await getEmployeeScoreHeatmap(params);
        if (res.code === 200) {
          this.heatmapData = res.data;
          this.renderHeatmapChart();
        }
      } catch (error) {
        console.error('获取热力图数据失败:', error);
      }
    },
    
    // 获取员工详情数据
    async fetchEmployeeData() {
      try {
        const params = {
          page: this.currentPage,
          size: this.itemsPerPage,
          timeRange: this.timeRange,
          departmentId: this.departmentId || '',
          query: this.searchQuery
        };
        const res = await getEmployeeScoreDetails(params);
        if (res.code === 200) {
          this.employeeData = res.data;
        }
      } catch (error) {
        console.error('获取员工详情数据失败:', error);
      }
    },
    
    // 渲染趋势图
    renderTrendChart() {
      if (!this.scoreChart) return;
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['总积分', '政治积分', '业务积分', '荣誉积分'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.trendData.labels || []
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '总积分',
            type: 'line',
            stack: '总量',
            data: this.trendData.totalScores || [],
            lineStyle: { width: 3 },
            itemStyle: { color: '#FA5050' }
          },
          {
            name: '政治积分',
            type: 'line',
            stack: '分类',
            data: this.trendData.politicalScores || [],
            itemStyle: { color: '#FF6B6B' }
          },
          {
            name: '业务积分',
            type: 'line',
            stack: '分类',
            data: this.trendData.professionalScores || [],
            itemStyle: { color: '#4ECDC4' }
          },
          {
            name: '荣誉积分',
            type: 'line',
            stack: '分类',
            data: this.trendData.honorScores || [],
            itemStyle: { color: '#FFD166' }
          }
        ]
      };
      
      this.scoreChart.setOption(option);
    },
    
    // 渲染类别分布图
    renderCategoryChart() {
      if (!this.categoryChart) return;
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 10,
          data: this.categoryData.categories.map(item => item.name)
        },
        series: [
          {
            name: '积分分布',
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '18',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: this.categoryData.categories.map(item => ({
              value: item.value,
              name: item.name,
              itemStyle: { color: item.color }
            }))
          }
        ]
      };
      
      this.categoryChart.setOption(option);
    },
    
    // 渲染部门对比图
    renderDepartmentChart() {
      if (!this.departmentChart) return;
      
      const departments = this.departmentData.departments || [];
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          boundaryGap: [0, 0.01]
        },
        yAxis: {
          type: 'category',
          data: departments.map(item => item.name),
          axisLabel: {
            interval: 0,
            rotate: 30
          }
        },
        series: [
          {
            name: '部门积分',
            type: 'bar',
            data: departments.map(item => ({
              value: item.score,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: '#FA5050' },
                  { offset: 1, color: '#FF9B9B' }
                ])
              }
            }))
          }
        ]
      };
      
      this.departmentChart.setOption(option);
    },
    
    // 渲染热力图
    renderHeatmapChart() {
      if (!this.heatmapChart) return;
      
      const { heatmapData, departments, levels } = this.heatmapData;
      
      // 转换数据为热力图格式
      const data = [];
      const max = Math.max(...heatmapData.map(item => item.count), 0);
      
      heatmapData.forEach(item => {
        const deptIndex = departments.indexOf(item.department);
        const levelIndex = levels.indexOf(item.level);
        if (deptIndex >= 0 && levelIndex >= 0) {
          data.push([deptIndex, levelIndex, item.count]);
        }
      });
      
      const option = {
        tooltip: {
          position: 'top',
          formatter: function(params) {
            return `${departments[params.data[0]]} - ${levels[params.data[1]]}: ${params.data[2]}人`;
          }
        },
        grid: {
          height: '70%',
          top: '10%'
        },
        xAxis: {
          type: 'category',
          data: departments,
          splitArea: {
            show: true
          },
          axisLabel: {
            interval: 0,
            rotate: 30
          }
        },
        yAxis: {
          type: 'category',
          data: levels,
          splitArea: {
            show: true
          }
        },
        visualMap: {
          min: 0,
          max: max,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: '5%',
          inRange: {
            color: ['#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695']
          }
        },
        series: [{
          name: '员工人数',
          type: 'heatmap',
          data: data,
          label: {
            show: true
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };
      
      this.heatmapChart.setOption(option);
    },
    
    // 搜索方法
    handleSearch() {
      this.currentPage = 1;
      this.fetchEmployeeData();
    }
  }
}
</script> 